import { useEffect, useReducer, useState } from 'react';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { estimateRevenue } from '../../utils/estimateRevenue';
import { reverseCity } from '../../utils/expressHelpers';
import useExpressAnnonce, { errorMessage } from '../../utils/hook/useExpressAnnonce';
import './AnnonceExpress.css';
import StepDocuments from './StepDocuments';
import StepQuestions from './StepQuestions';
import StepReview from './StepReview';

const ADVANCED_ROUTE = '/louer-voiture'; // adaptez : votre page LouerVoiture
const STEPS = ['docs', 'questions', 'review'];
const LABELS = ['Documents', 'Votre voiture', 'Relecture'];

const initial = {
  registrationFile: null,
  photos: [], // { id, preview, url, status: 'uploading' | 'done' | 'error' }
  analysis: null,
  coverUrl: null,
  modele: null, // { id, marque, modele }
  annee: String(new Date().getFullYear() - 3),
  carburantType: '2',
  places: 5,
  portes: 5,
  isUtilitaire: false,
  kilometrage: '2',
  vitesseType: '0',
  place: null, // { label, lat, lng }
  options: [],
  detected: [],
  preview: null,
  specKey: null, // empreinte des infos envoyées à /preview
  fallbackPrice: null,
  price: '',
  description: '',
  schedule: { days: [0, 1, 2, 3, 4, 5, 6], from: '08:00', until: '20:00' },
  published: null,
};

// patch = objet ou fonction (état) => objet
const reducer = (state, patch) => ({
  ...state,
  ...(typeof patch === 'function' ? patch(state) : patch),
});

const buildSpec = (s) => ({
  modeleId: s.modele?.id,
  annee: s.annee,
  carburantType: s.carburantType,
  vitesseType: s.vitesseType,
  kilometrage: s.kilometrage,
  places: s.places,
  portes: s.portes,
  isUtilitaire: s.isUtilitaire,
  options: s.options,
  coords: { lat: s.place?.lat, lng: s.place?.lng },
});

const orderedUrls = (s) => {
  const urls = s.photos.filter((p) => p.url).map((p) => p.url);
  return s.coverUrl ? [s.coverUrl, ...urls.filter((u) => u !== s.coverUrl)] : urls;
};

function AnnonceExpressPage() {
  const api = useExpressAnnonce();
  const [state, patch] = useReducer(reducer, initial);
  const [step, setStep] = useState('docs');
  const [busy, setBusy] = useState(false);

  // Chaque écran s'ouvre en haut de page (sinon l'aperçu reste hors écran)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [step]);

  const runAnalysis = async () => {
    setBusy(true);
    try {
      const urls = state.photos.filter((p) => p.url).map((p) => p.url);
      const data = await api.analyze(state.registrationFile, urls);
      const v = data.vehicle;
      const best = v?.modelCandidates?.[0];
      const visible = data.photos?.equipementsVisibles || [];
      const coverIndex = data.photos?.bestCoverIndex;

      patch((s) => ({
        analysis: data,
        coverUrl: Number.isInteger(coverIndex) ? (urls[coverIndex] ?? null) : null,
        modele: best?.score === 1 ? best : s.modele,
        ...(v?.annee ? { annee: String(v.annee) } : {}),
        ...(v?.carburantType != null ? { carburantType: String(v.carburantType) } : {}),
        ...(v?.places ? { places: v.places } : {}),
        isUtilitaire: /utilitaire/i.test(data.photos?.carrosserie || ''),
        detected: visible.map((e) => e.id),
        options: visible.map((e) => e.id), // présélectionnés, à confirmer à l'écran suivant
      }));
      setStep('questions');
    } catch (err) {
      if (err?.response?.status === 422) {
        toast.error(errorMessage(err));
      } else {
        toast.info('Analyse indisponible : complétez les informations à la main.');
        setStep('questions');
      }
    } finally {
      setBusy(false);
    }
  };

  const handlePlace = async (loc) => {
    const [lng, lat] = loc.center || [];
    if (typeof lat !== 'number' || typeof lng !== 'number') return;
    let label = loc.place_name;
    if (label === 'Position actuelle') label = (await reverseCity(lat, lng)) || label;
    patch({ place: { label, lat, lng } });
  };

  const goReview = async () => {
    const spec = buildSpec(state);
    const specKey = JSON.stringify(spec);

    // Rien n'a changé depuis le dernier aperçu : on le réutilise (pas de nouvel appel IA)
    if (state.preview && state.specKey === specKey) {
      setStep('review');
      return;
    }

    setBusy(true);
    try {
      const data = await api.preview(spec);

      let fallback = null;
      try {
        fallback = estimateRevenue({
          marque: state.modele?.marque,
          locationName: state.place?.label,
          annee: state.annee,
          km: state.kilometrage,
        }).daily;
      } catch (e) {
        console.warn('estimateRevenue indisponible', e);
      }

      patch({
        preview: data,
        specKey,
        description: data.description,
        fallbackPrice: fallback,
        price: String(data.price?.median ?? fallback ?? ''),
      });
      setStep('review');
    } catch (err) {
      console.error('goReview', err);
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const data = await api.publish({
        ...buildSpec(state),
        draftId: state.preview.draftId,
        description: state.description,
        price: Number(state.price),
        photoUrls: orderedUrls(state),
        schedule: state.schedule,
        placeLabel: state.place.label,
      });
      patch({ published: data });
      setStep('done');
    } catch (err) {
      console.error('publish', err);
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const index = STEPS.indexOf(step);

  return (
    <main className="mc-home mc-form-page">
      <ToastContainer />
      <div className="mc-form-shell">
        {step !== 'done' && (
          <div className="mc-stepper">
            <div className="mc-stepper-top">
              <span>Annonce express</span>
              <span>
                Étape {index + 1} sur 3 · {LABELS[index]}
              </span>
            </div>
            <div
              className="mc-stepper-bar"
              role="progressbar"
              aria-valuenow={(index + 1) * 33}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <div className="mc-stepper-fill" style={{ width: `${(index + 1) * 33}%` }}></div>
            </div>
          </div>
        )}

        {step === 'docs' && (
          <StepDocuments
            state={state}
            patch={patch}
            busy={busy}
            onAnalyze={runAnalysis}
            onSkip={() => setStep('questions')}
          />
        )}
        {step === 'questions' && (
          <StepQuestions
            state={state}
            patch={patch}
            busy={busy}
            onPlace={handlePlace}
            onBack={() => setStep('docs')}
            onNext={goReview}
          />
        )}
        {step === 'review' && (
          <StepReview
            state={state}
            patch={patch}
            busy={busy}
            onBack={() => setStep('questions')}
            onPublish={publish}
          />
        )}

        {step === 'done' && (
          <section className="mc-form-card">
            <span className="mc-section-kicker">C'EST ENVOYÉ</span>
            <h1 className="mc-form-title">Votre annonce est en cours de vérification</h1>
            <p className="mc-form-subtitle">
              Elle sera en ligne dès que nos équipes auront validé vos documents. Ajoutez-les
              maintenant pour accélérer la mise en ligne :
            </p>
            <ul>
              {(state.published?.missingDocs || []).map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
            <div className="mc-form-actions">
              <span></span>
              <Link to="/account/voitures" className="mc-btn mc-btn--primary">
                Ajouter mes documents
                <ion-icon name="arrow-forward-outline"></ion-icon>
              </Link>
            </div>
          </section>
        )}

        {step !== 'done' && (
          <p className="mc-field-hint">
            Vous préférez tout détailler ?{' '}
            <Link to={ADVANCED_ROUTE}>Utiliser le formulaire détaillé</Link>
          </p>
        )}
      </div>
    </main>
  );
}

export default AnnonceExpressPage;
